import { inject, Injectable } from '@angular/core';
import {
  deleteObject,
  FullMetadata,
  getDownloadURL,
  getMetadata,
  ref,
  Storage,
  uploadBytes,
  UploadResult
} from '@angular/fire/storage';
import { DownloadFileCtx } from 'src/lib/fliko/models/http/downloadFile.data';
import { ErrorHandlingService } from 'src/app/shared/error-handling/error-handling.service';
import { FunctionsApiService, Result } from 'src/app/shared/services/functions-api.service';
import { FileInStorageData } from '../models/file.data';
import { PushIdService } from './pushId.service';

@Injectable({
  providedIn: 'root'
})
export class FireStorageService {
  private storage = inject(Storage);
  constructor(
    private pushId: PushIdService,
    private fas: FunctionsApiService,
    private errorService: ErrorHandlingService
  ) {}

  private onReject = (reason: any) => {
    if (reason.code == 'functions/permission-denied') {
      this.errorService.showGlobalError(this.errorService.getErrorText('permDenied') ?? reason.message);
    } else if (reason.code == 'functions/not-found' || reason.code == 'functions/internal') {
      this.errorService.showGlobalError(this.errorService.getErrorText('fileNotFound') ?? reason.message);
    } else if (reason.code == 'functions/invalid-argument') {
      this.errorService.showGlobalError(this.errorService.getErrorText('invalidFile') ?? reason.message);
    }
  };

  /**
   * Get signed url for file in storage. Used for downloading bypassing CORS.
   * @param {DownloadFileCtx} data file context
   * @return {Result<string>} signed url or error
   */
  public getSignedUrl(data: DownloadFileCtx): Result<string> {
    return this.fas.callFunction<string>('downloadFromStorage', data, this.onReject);
  }

  /**
   * Upload file to storage and get its data
   * @param {File[]}  files       list of files to upload
   * @param {string}  storagePath
   * @param {boolean} url         should algorithm get downloadURL from storage?
   * @return {Promise<FileInStorageData[]>}
   */
  public async uploadFilesToStorage(files: File[], storagePath: string, url: boolean = true): Promise<FileInStorageData[]> {
    const ret: FileInStorageData[] = [];
    for (const file of files) {
      const sRef = ref(this.storage, `${storagePath}/${this.pushId.create()}-${file.name}`);
      const result = await uploadBytes(sRef, file);
      if (url) {
        await getDownloadURL(result.ref).then((data) => {
          ret.push({ downloadUrl: data, name: file.name, storagePath: result.ref.fullPath, size: file.size });
        });
      } else {
        ret.push({ name: file.name, storagePath: result.ref.fullPath, size: file.size });
      }
    }
    return ret;
  }

  /**
   * Get file metadata from storage
   * @param {string} url file url, ALSO WORKS ON STORAGE PATH
   * @return
   */
  public getMetadata(url: string): Promise<FullMetadata> {
    return getMetadata(ref(this.storage, url));
  }

  public uploadFile(file: File | Blob, path: string, metadata?: any): Promise<UploadResult> {
    return uploadBytes(ref(this.storage, path), file, metadata);
  }

  public deleteFile(storagePath: string): Promise<void> {
    return deleteObject(ref(this.storage, storagePath));
  }

  public getDownloadURL(storagePath: string): Promise<string> {
    return getDownloadURL(ref(this.storage, storagePath));
  }
}
